import {
    ChangeEventHandler,
    FC,
    useEffect,
    useCallback,
    useMemo,
    useState,
} from 'react';
import styled, { css, useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { BuyButton, BuyButtonRawProps } from '../BuyButton';
import { CardIdText } from '../CardId';
import { CardNumber75Container, CardNumber75Text } from '../CardNumber75';
import { CountdownTimer } from '../CountdownTimer';
import { GameTypeNumbers } from '../GameType';
import { ModalLabel } from '../ModalLabel';
import { ModalTitle } from '../ModalTitle';
import { SpecialBanner } from '../SpecialBanner';
import { BingoCard80, CardRow as CardRow80 } from '../BingoCard80';
import { CardNumber30Container, CardNumber30Text } from '../CardNumber30';
import { CardNumber90Container, CardNumber90Text } from '../CardNumber90';
import {
    BingoCard30,
    CardRow as CardRow30,
    CardText as CardText30,
} from '../BingoCard30';
import {
    BingoCard75,
    CardNumber75Middle,
    CardRow as CardRow75,
    CardText,
} from '../BingoCard75';
import {
    BingoCard90,
    CardRow as CardRow90,
    CardTogoContainer,
} from '../BingoCard90';
import {
    CardNumber80Container,
    CardNumber80Text,
    ContainerStick,
} from '../CardNumber80';
import {
    CommonModal,
    CommonModalContainer,
    CommonModalProps,
} from '../CommonModal';
import {
    StripContainer,
    CardsContainer,
    StripId,
} from '../BingoCardList/mobile';
import { useSoundHandler } from '../../hooks/use-sound-handler';
import {
    CommonToggle,
    CommonToggleInput,
    CommonToggleSlider,
} from '../CommonToggle';

type ButtonProps = Omit<BuyButtonRawProps, 'handleClick' | 'total'> & {
    handleBuy: (props: { tickets: number }) => void;
};

type CardsItem = {
    cardId: string;
    cardNumbers: number[];
};

type StrippedCard = {
    type: 'stripped';
    list: Array<{ stripId: string; cards: CardsItem[] }>;
};

type RegularCard = {
    type: 'regular';
    list: CardsItem[];
};

type PickCardsModalProps = CommonModalProps &
    ButtonProps & {
        cards: RegularCard | StrippedCard;
        bought: string[];
        selected: string[];
        gameType: GameTypeNumbers;
        stripActive: boolean;
        stripsOnly: boolean;
        hasFreeSpace: boolean;
        hasNext?: boolean;
        special: string;
        gameCurrency: string;
        ticketPrice: number;
        gameCardsMax: number;
        freeCards: number;
        fixedGame: number | undefined;
        countdownDate: Date;
        handleNewCards: () => void;
        handleSelected: (map: boolean[] | boolean[][]) => void;
        tokensFirst?: boolean;
        onTokensFirstChange?: ChangeEventHandler<HTMLInputElement>;
        formatCurrency: (value: number, to?: string) => string;
    };

const CARD_STYLES = {
    30: css`
        width: 16.125rem;
        height: 17.5rem;
        padding: 0.688rem 0.313rem 0.438rem 0.313rem;
    `,
    75: css`
        width: 16.125rem;
        height: 17.5rem;
        padding: 0.688rem 0.313rem 0.438rem 0.313rem;
    `,
    80: css`
        width: 16.125rem;
        height: 15.063rem;
        padding: 1.125rem 0.625rem 0.188rem 0.625rem;
    `,
    90: css`
        width: 33.75rem;
        height: 10.938rem;
        padding: 1rem 0.813rem 0.688rem;
    `,
};

const PickCardsModalContainer = styled(CommonModalContainer)`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 39.375rem;
    height: 66.875rem;
    padding: 1.875rem 0.438rem 1.875rem 1.563rem;
    margin-bottom: 8rem;
`;

const PickCardsTitle = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    margin-bottom: 1rem;
    width: 80%;
`;

const ModalTitleWrapper = styled(ModalTitle)`
    font-size: 2rem;
    line-height: 2rem;
    text-align: center;
    margin-right: 0.313rem;
`;

const TopActions = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    gap: 1rem;
    margin-bottom: 1.188rem;
    padding-right: 0.75rem;
`;

const SpecialBannerSection = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
    min-width: 0;
`;

const SpecialBannerWrapper = styled(SpecialBanner)`
    margin-left: 1.438rem;
`;

const ModalLabelWrapper = styled(ModalLabel)`
    font-size: 2.5rem;
    margin-bottom: 0.625rem;
`;

const MaxCardWrapper = styled.div`
    font-family: Signika-Medium;
    font-size: 1.25rem;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: center;
    margin-bottom: 1.688rem;
    color: ${({ theme }) => theme.pickCardsModal.color};
`;

const TimerWrapper = styled(CountdownTimer)`
    font-size: 2rem;
    line-height: 2rem;
    margin-top: 0.188rem;
`;

const BingoCardListContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    row-gap: 1.25rem;
    width: 100%;
    height: 41.063rem;
    overflow-x: auto;
    padding-right: 0.75rem;
    padding-bottom: 0.75rem;

    &::-webkit-scrollbar-track {
        border-radius: 0.438rem;
        background-color: ${({ theme: { pickCardsModal } }) =>
            pickCardsModal.scroll.track};
    }

    &::-webkit-scrollbar {
        width: 0.625rem;
        background-color: transparent;
    }

    &::-webkit-scrollbar-thumb {
        border-radius: 0.75rem;
        background-color: ${({ theme: { pickCardsModal } }) =>
            pickCardsModal.scroll.thumb};
    }
`;

const BingoCardListItem = styled.div<{ gameType: number }>`
    ${({ gameType }) => CARD_STYLES[gameType as keyof typeof CARD_STYLES]};

    /* 30BALL overrides */
    ${CardRow30} {
        grid-row-gap: 0.6rem;
    }

    ${CardNumber30Container} {
        width: 4.375rem;
        height: 3.525rem;
    }

    ${CardNumber30Text} {
        font-size: 1.5rem;
    }

    ${CardText30} {
        font-size: 1.5rem;
    }

    /* 75BALL overrides */
    ${CardRow75} {
        grid-row-gap: 0.188rem;
    }

    ${CardText} {
        font-size: 1.75rem;
    }

    ${CardNumber75Middle} {
        width: 2.438rem;
        height: 2.313rem;
    }

    ${CardNumber75Container} {
        width: 2.938rem;
        height: 2.313rem;
    }

    ${CardNumber75Text} {
        font-size: 1.556rem;
    }

    /* 80BALL overrides */
    ${CardRow80} {
        grid-row-gap: 0.625rem;
    }

    ${CardNumber80Container} {
        width: 3.188rem;
        height: 2.438rem;
    }

    ${ContainerStick} {
        width: 0.5rem;
        height: 2.563rem;
    }

    ${CardNumber80Text} {
        font-size: 1.563rem;
    }

    /* 90BALL overrides */
    ${CardRow90} {
        grid-gap: 0.5rem;
    }

    ${CardNumber90Container} {
        width: 2.75rem;
        height: 2.75rem;
    }

    ${CardNumber90Text} {
        font-size: 1.563rem;
    }

    ${CardTogoContainer} {
        flex-basis: 5%;
    }

    ${CardIdText} {
        font-size: 0.975rem;
    }
`;

const PickCardsFooter = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 11.25rem;
`;

const NewCardsButton = styled.div<{ disabled: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    width: 8.125rem;
    height: 6.25rem;
    border-radius: 1.438rem;
    cursor: pointer;

    ${({ disabled, theme: { pickCardsModal } }) => `
        border: solid 0.113rem ${pickCardsModal.border};
        background-image: linear-gradient(to bottom, ${
            pickCardsModal.backgroundGradient.upper
        }, ${pickCardsModal.backgroundGradient.lower} 99%);
        box-shadow: 0 0.375rem 0 0 ${
            pickCardsModal.boxShadow.first
        }, inset 0 0.313rem 0 0 ${pickCardsModal.boxShadow.second},
            inset 0 -0.313rem 0 0 ${pickCardsModal.boxShadow.third};
        opacity: ${disabled ? 0.5 : 1};
        pointer-events: ${disabled ? 'none' : 'auto'};

    `};
`;

const NewCardsIcon = styled.img`
    width: 2.813rem;
    height: 3.125rem;
`;

const NewCardsText = styled.div`
    font-family: Signika-Bold;
    font-size: 1.125rem;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
`;

const BuyButtonWrapper = styled(BuyButton)`
    width: 24.875rem;
    height: 6.25rem;
`;

const PurchaseControls = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    width: 24.875rem;
`;

const UseTokensFirstSection = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    width: 100%;
    font-family: Signika-Bold;
    font-size: 1.5rem;
    font-weight: bold;
    color: #fff;
`;

const UseTokensToggle = styled(CommonToggle)`
    width: 5.25rem;
    height: 2.5rem;

    ${CommonToggleSlider} {
        border-radius: 1.25rem;

        &:before {
            width: 2.25rem;
            height: 2.25rem;
            left: 0.188rem;
            bottom: 0.125rem;
        }
    }

    ${CommonToggleInput}:checked + ${CommonToggleSlider}:before {
        transform: translateX(2.6rem);
    }
`;

const BINGO_CARD_COMPONENT = {
    75: BingoCard75,
    80: BingoCard80,
    90: BingoCard90,
    30: BingoCard30,
};

const allTrue = (arr: boolean[]) => arr.every((v) => v === true);

export const PickCardsModalMobile: FC<PickCardsModalProps> = ({
    cards,
    bought,
    selected,
    gameType,
    stripActive,
    stripsOnly,
    fixedGame,
    hasFreeSpace,
    hasNext,
    special,
    countdownDate,
    isDisabled,
    ticketPrice,
    gameCardsMax,
    gameCurrency,
    freeCards,
    handleBuy,
    handleSelected,
    handleNewCards,
    consumedTokens,
    tokensFirst,
    onTokensFirstChange,
    formatCurrency,
    ...props
}) => {
    const { t } = useTranslation();

    const theme = useTheme();
    const { newCards } = theme.images;
    const BingoCard = BINGO_CARD_COMPONENT[gameType];
    const boughtTickets = bought.length - freeCards;

    const initialcardsMap = useMemo(() => {
        const toggles =
            cards.type === 'regular'
                ? cards.list.map(
                      (card) =>
                          bought.includes(card.cardId) ||
                          selected.includes(card.cardId)
                  )
                : cards.list.map((item) => {
                      return item.cards.map(
                          (card) =>
                              bought.includes(
                                  item.stripId + '-' + card.cardId
                              ) ||
                              selected.includes(
                                  item.stripId + '-' + card.cardId
                              )
                      );
                  });

        const tickets = selected.length;
        let total = formatCurrency(tickets * ticketPrice, gameCurrency);

        if (fixedGame) {
            total = formatCurrency(ticketPrice, gameCurrency);
        }

        return {
            toggles,
            tickets,
            total,
            max: gameCardsMax,
        };
    }, [
        bought,
        selected,
        cards,
        gameCardsMax,
        ticketPrice,
        gameCurrency,
        fixedGame,
        formatCurrency,
    ]);

    const initialStripsMap = useMemo(
        () => initialcardsMap.toggles.map(() => true),
        [initialcardsMap.toggles]
    );

    const [map, setMap] = useState(initialcardsMap);
    const [stripMap, setStripMap] = useState(initialStripsMap);

    const $disabled =
        isDisabled || fixedGame ? map.tickets < gameCardsMax : false;

    const handleSubmission = useCallback(() => {
        handleBuy({
            tickets: map.tickets,
        });
    }, [handleBuy, map.tickets]);

    const checkStrip = (cardsToggle: boolean[][]) => {
        const activeStrip = cardsToggle.find((card: boolean[]) => {
            if (Array.isArray(card)) {
                return card.includes(true) && !allTrue(card);
            }

            return [];
        });

        const activeStripIndex = cardsToggle.indexOf(activeStrip || []);

        const toggles = cardsToggle.map((_card: boolean[], index: number) => {
            if (!activeStrip) {
                return true;
            }

            if (index === activeStripIndex) {
                return true;
            }

            return false;
        });

        if (activeStrip) {
            toggles[activeStripIndex] = true;
        }

        setStripMap(toggles);
    };

    const handleClick = useCallback(
        (index: number, stripIndex?: number) => {
            setMap((prevMap) => {
                const condition =
                    typeof stripIndex === 'number'
                        ? !(prevMap.toggles[stripIndex] as boolean[])[index]
                        : !prevMap.toggles[index];

                // prevents player from selecting cards if max tickets have been selected.
                // "condition" is needed so that the player can unselect a card when max tickets have been selected.
                if (
                    prevMap.tickets + boughtTickets === prevMap.max &&
                    condition
                ) {
                    return prevMap;
                }

                // select/unselect logic
                const sliced = prevMap.toggles.slice(0);

                if (typeof stripIndex === 'number' && stripsOnly) {
                    const strip = (sliced[stripIndex] as boolean[]).map(
                        (item) => !item
                    );
                    (sliced[stripIndex] as boolean[]) = strip;
                } else if (typeof stripIndex === 'number') {
                    (sliced[stripIndex] as boolean[])[index] = !(
                        sliced[stripIndex] as boolean[]
                    )[index];
                } else {
                    sliced[index] = !sliced[index];
                }

                // logic for adding and subtracting "number of tickets"
                let add: number = 0;

                if (typeof stripIndex === 'number' && stripsOnly) {
                    (sliced[stripIndex] as boolean[])[index]
                        ? (add = gameType === 90 ? 6 : 3)
                        : (add = gameType === 90 ? -6 : -3);
                } else if (typeof stripIndex === 'number') {
                    (sliced[stripIndex] as boolean[])[index]
                        ? (add = 1)
                        : (add = -1);
                } else {
                    sliced[index] ? (add = 1) : (add = -1);
                }

                const tickets = prevMap.tickets + add;
                let total = formatCurrency(tickets * ticketPrice, gameCurrency);

                if (fixedGame) {
                    total = formatCurrency(ticketPrice, gameCurrency);
                }

                handleSelected(sliced);

                return {
                    ...prevMap,
                    toggles: sliced,
                    tickets,
                    total,
                };
            });
        },
        [
            ticketPrice,
            stripsOnly,
            fixedGame,
            gameType,
            gameCurrency,
            boughtTickets,
            setMap,
            handleSelected,
            formatCurrency,
        ]
    );

    useEffect(() => {
        setMap(initialcardsMap);
    }, [initialcardsMap]);

    useEffect(() => {
        // check if a strip of cards should be clickable or not
        // only for strip_active: true and strip_only: false
        if (stripActive) {
            checkStrip(map.toggles as boolean[][]);
        }
    }, [stripActive, map.toggles]);

    const $handleNewCards = useSoundHandler(handleNewCards, 'click');
    const $handleSubmission = useSoundHandler(handleSubmission, 'buy');

    return (
        <CommonModal {...props} Container={PickCardsModalContainer}>
            <PickCardsTitle>
                <ModalTitleWrapper title={t('modalPickCards.nextGame')} />
                <TimerWrapper to={countdownDate} />
            </PickCardsTitle>
            <TopActions>
                <NewCardsButton
                    onClick={$handleNewCards}
                    disabled={hasNext === false}
                >
                    <NewCardsIcon src={newCards} alt="New Cards Image" />
                    <NewCardsText>{t('modalPickCards.newCards')}</NewCardsText>
                </NewCardsButton>
                <SpecialBannerSection>
                    {special && <SpecialBannerWrapper text={special} />}
                </SpecialBannerSection>
            </TopActions>
            <ModalLabelWrapper label={t('modalPickCards.selectCards')} />
            {map.tickets + boughtTickets === map.max && (
                <MaxCardWrapper>{t('modalPickCards.maxCards')}</MaxCardWrapper>
            )}
            <BingoCardListContainer>
                {cards.type === 'regular' && map.toggles.length > 0
                    ? cards.list.map(({ cardId, cardNumbers }, index) => {
                          const isBought = bought.includes(cardId);
                          return (
                              <BingoCardListItem
                                  as={BingoCard}
                                  key={cardId}
                                  cardId={cardId}
                                  tiles={cardNumbers}
                                  gameType={gameType}
                                  hasFreeSpace={hasFreeSpace}
                                  autoDaub={true}
                                  pickCards={true}
                                  picked={map.toggles[index] as boolean}
                                  bought={isBought}
                                  cardIndex={index}
                                  handleClick={
                                      isBought ? undefined : handleClick
                                  }
                              />
                          );
                      })
                    : null}
                {cards.type === 'stripped' && map.toggles.length > 0
                    ? cards.list.map((item, stripIndex) => (
                          <StripContainer
                              key={stripIndex}
                              active={stripMap[stripIndex]}
                          >
                              <StripId>{`${t('modalPurchase.cardTypeStrips')} ${
                                  item.stripId
                              }`}</StripId>
                              <CardsContainer>
                                  {item.cards.map(
                                      ({ cardId, cardNumbers }, index) => {
                                          const isBought = bought.includes(
                                              item.stripId + '-' + cardId
                                          );

                                          const picked =
                                              map.toggles[stripIndex] ||
                                              map.toggles[0];

                                          return (
                                              <BingoCardListItem
                                                  as={BingoCard}
                                                  key={cardId}
                                                  cardId={cardId}
                                                  tiles={cardNumbers}
                                                  gameType={gameType}
                                                  hasFreeSpace={hasFreeSpace}
                                                  autoDaub={true}
                                                  pickCards={true}
                                                  picked={
                                                      (picked as boolean[])[
                                                          index
                                                      ]
                                                  }
                                                  bought={isBought}
                                                  cardIndex={index}
                                                  stripIndex={stripIndex}
                                                  handleClick={
                                                      isBought
                                                          ? undefined
                                                          : handleClick
                                                  }
                                              />
                                          );
                                      }
                                  )}
                              </CardsContainer>
                          </StripContainer>
                      ))
                    : null}
            </BingoCardListContainer>
            <PickCardsFooter>
                <PurchaseControls>
                    <UseTokensFirstSection>
                        <span>{t('modalPurchase.tokensFirst')}</span>
                        <UseTokensToggle
                            checked={tokensFirst}
                            onChange={onTokensFirstChange}
                        />
                    </UseTokensFirstSection>
                    <BuyButtonWrapper
                        isDisabled={$disabled}
                        withTotal={true}
                        tickets={map.tickets}
                        total={map.total}
                        consumedTokens={consumedTokens}
                        handleClick={$handleSubmission}
                    />
                </PurchaseControls>
            </PickCardsFooter>
        </CommonModal>
    );
};
